/**
 * Employee Web — `/me/expenses/new` create-and-redirect route.
 *
 * Phase 19 Plan 01. Renders a tiny bootstrapping component that calls
 * `useCreateExpenseReport` once with a default title + zero lines, then
 * navigates to `/me/expenses/$id?mode=new`. Permission gate:
 * `expense:request:self`.
 *
 * The user perceives a brief "Creating draft…" state then lands on the
 * editor surface ready to add lines.
 */
import { useEffect, type JSX } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "@linkhr/i18n";
import { Alert, Button, Icon, PageHeader, Spinner } from "@linkhr/design-system";
import { RefreshCw } from "lucide-react";
import { requirePermission } from "@linkhr/auth";
import { useCreateExpenseReport, useTenantSlug } from "@linkhr/api-client";

export const Route = createFileRoute("/_auth/me/expenses/new")({
  staticData: { breadcrumb: { labelKey: "expenses:me.expenses.new.title" } },
  beforeLoad: requirePermission({
    anyOf: ["expense:request:self"],
    defaultRoute: "/me/overview",
  }),
  component: NewExpenseRoute,
});

function NewExpenseRoute(): JSX.Element {
  const { t } = useTranslation("expenses");
  const tenantSlug = useTenantSlug();
  const navigate = useNavigate();
  const createMut = useCreateExpenseReport(tenantSlug);

  useEffect(() => {
    if (createMut.isPending || createMut.isSuccess) return;
    createMut.mutate(
      { title: t("me.expenses.new.default_title"), lines: [] },
      {
        onSuccess: ({ report }) => {
          void navigate({
            to: "/me/expenses/$id",
            params: { id: report.id },
            search: { mode: "new" },
            replace: true,
          });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="flex flex-col gap-lg">
      <PageHeader title={t("me.expenses.new.title")} subtitle={t("me.expenses.new.creating")} />
      {createMut.isError ? (
        <Alert
          variant="danger"
          title={t("me.expenses.new.create_failed")}
          description={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                createMut.reset();
                createMut.mutate(
                  { title: t("me.expenses.new.default_title"), lines: [] },
                  {
                    onSuccess: ({ report }) => {
                      void navigate({
                        to: "/me/expenses/$id",
                        params: { id: report.id },
                        search: { mode: "new" },
                        replace: true,
                      });
                    },
                  },
                );
              }}
            >
              <Icon as={RefreshCw} size={14} aria-label="" />
              <span className="ms-xs">{t("admin.categories.list.actions.retry")}</span>
            </Button>
          }
        />
      ) : (
        <span className="flex items-center gap-sm text-muted">
          <Spinner size="sm" />
          <span className="ms-xs">{t("me.expenses.new.creating")}</span>
        </span>
      )}
    </section>
  );
}
